import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Instagram } from 'lucide-react';
import { clinicInfo } from '../data/mockData';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer style={{ 
      background: 'var(--bg-secondary)', 
      borderTop: '1px solid var(--border-light)',
      padding: '80px 0 24px'
    }}>
      <div className="container">
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '48px',
          marginBottom: '48px'
        }}>
          {/* About Section */}
          <div>
            <h3 className="heading-3" style={{ marginBottom: '16px' }}>
              {clinicInfo.name}
            </h3>
            <p className="body-small" style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              {clinicInfo.tagline}
            </p>
            <div style={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
              <a 
                href={clinicInfo.instagram}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  width: '40px',
                  height: '40px',
                  border: '1px solid var(--border-medium)',
                  borderRadius: '0px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-primary)',
                  transition: 'all 0.2s ease'
                }}
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="heading-3" style={{ marginBottom: '16px' }}>
              Quick Links
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                { to: '/about', label: 'About Us' },
                { to: '/services', label: 'Services' },
                { to: '/doctor', label: 'Our Doctor' },
                { to: '/gallery', label: 'Gallery' },
                { to: '/testimonials', label: 'Testimonials' },
                { to: '/blog', label: 'Blog' }
              ].map((link) => (
                <li key={link.to} style={{ marginBottom: '12px' }}>
                  <Link 
                    to={link.to}
                    className="body-small"
                    style={{ 
                      color: 'var(--text-secondary)',
                      textDecoration: 'none',
                      transition: 'color 0.2s ease'
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="heading-3" style={{ marginBottom: '16px' }}>
              Our Services
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                'Dental Implants',
                'Clear Aligners',
                'Root Canal Treatment',
                'Cosmetic Dentistry',
                'Teeth Whitening',
                'Emergency Care'
              ].map((service) => (
                <li key={service} style={{ marginBottom: '12px' }}>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {service}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="heading-3" style={{ marginBottom: '16px' }}>
              Contact Us
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <MapPin size={18} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: '2px' }} />
                <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  {clinicInfo.address}
                </span>
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <Phone size={18} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                <a 
                  href={`tel:${clinicInfo.phone}`}
                  className="body-small"
                  style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                >
                  {clinicInfo.phone}
                </a>
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <Mail size={18} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                <a 
                  href={`mailto:${clinicInfo.email}`}
                  className="body-small"
                  style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                >
                  {clinicInfo.email}
                </a>
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <Clock size={18} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  {clinicInfo.workingHours}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div style={{ 
          borderTop: '1px solid var(--border-light)',
          paddingTop: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignItems: 'center',
          textAlign: 'center'
        }}>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Link 
              to="/privacy-policy"
              className="body-small"
              style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms-conditions"
              className="body-small"
              style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
            >
              Terms & Conditions
            </Link>
            <Link 
              to="/disclaimer"
              className="body-small"
              style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
            >
              Disclaimer
            </Link>
          </div>
          
          <p className="body-small" style={{ color: 'var(--text-light)', margin: 0 }}>
            © {currentYear} {clinicInfo.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
